import React, { useEffect, useState } from 'react';
import { Activity, AlertTriangle, FileText, Filter, Loader2, RefreshCw, Search, Shield, TrendingUp, Users } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const getActionColor = (action) => {
  const map = {
    CREATE: 'bg-emerald-100 text-emerald-800',
    UPDATE: 'bg-blue-100 text-blue-800',
    DELETE: 'bg-red-100 text-red-800',
    LOGIN_SUCCESS: 'bg-emerald-100 text-emerald-800',
    LOGIN_FAILED: 'bg-red-100 text-red-800',
  };
  return map[action] || 'bg-muted text-muted-foreground';
};

const getActionIcon = (action) => {
  if (action.includes('LOGIN')) return '🔐';
  if (action === 'CREATE') return '➕';
  if (action === 'UPDATE') return '✏️';
  if (action === 'DELETE') return '🗑️';
  return '📝';
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-PT');
  } catch {
    return value;
  }
};

export default function AuditDashboard() {
  const [tab, setTab] = useState('overview');
  const [statistics, setStatistics] = useState(null);
  const [suspicious, setSuspicious] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    user_id: '',
    start_date: '',
    end_date: '',
    limit: 50,
    offset: 0,
  });

  const loadOverview = async () => {
    try {
      const [statsRes, suspiciousRes] = await Promise.all([
        api.get('/audit/statistics', { params: { days: 30 } }),
        api.get('/audit/suspicious', { params: { hours: 24 } }),
      ]);
      setStatistics(statsRes.data?.data || null);
      setSuspicious(suspiciousRes.data?.data || null);
    } catch {
      toast.error('Erro ao carregar dados de auditoria');
    }
  };

  const loadLogs = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const res = await api.get('/audit/logs', { params });
      setLogs(res.data?.data || []);
    } catch {
      toast.error('Erro ao carregar logs');
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadOverview();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (tab === 'logs') loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters]);

  const refresh = async () => {
    setLoading(true);
    await loadOverview();
    if (tab === 'logs') await loadLogs();
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Carregando Auditoria...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Auditoria</h1>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
          <RefreshCw size={16} className="mr-2" /> Atualizar
        </Button>
      </div>

      {suspicious &&
        (suspicious.failed_logins?.length > 0 ||
          suspicious.mass_deletes?.length > 0 ||
          suspicious.multiple_ips?.length > 0) && (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle size={18} /> Atividades Suspeitas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {suspicious.failed_logins?.length > 0 && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
                  🚨 {suspicious.failed_logins.length} tentativas de login falhadas
                  <ul className="mt-1 space-y-1 text-red-700">
                    {suspicious.failed_logins.map((item, i) => (
                      <li key={i}>• IP {item.ip_address}: {item.attempts} tentativas</li>
                    ))}
                  </ul>
                </div>
              )}
              {suspicious.mass_deletes?.length > 0 && (
                <div className="rounded-lg bg-orange-50 p-3 text-sm text-orange-800">
                  ⚠️ Exclusões em massa
                  <ul className="mt-1 space-y-1 text-orange-700">
                    {suspicious.mass_deletes.map((item, i) => (
                      <li key={i}>• {item.username}: {item.delete_count} exclusões de {item.entity_type}</li>
                    ))}
                  </ul>
                </div>
              )}
              {suspicious.multiple_ips?.length > 0 && (
                <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                  🌐 Múltiplos IPs
                  <ul className="mt-1 space-y-1 text-amber-700">
                    {suspicious.multiple_ips.map((item, i) => (
                      <li key={i}>• {item.username}: {item.ip_count} IPs diferentes</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

      <div className="border-b">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'logs', 'statistics'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`border-b-2 py-4 px-1 text-sm font-medium ${
                tab === t
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'overview' && 'Visão Geral'}
              {t === 'logs' && 'Logs'}
              {t === 'statistics' && 'Estatísticas'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && statistics && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total de Ações"
            value={statistics.action_stats?.reduce((sum, item) => sum + item.count, 0) || 0}
            icon={<Activity className="h-8 w-8 text-primary" />}
          />
          <MetricCard
            title="Usuários Ativos"
            value={statistics.top_users?.length || 0}
            icon={<Users className="h-8 w-8 text-emerald-600" />}
          />
          <MetricCard
            title="Tipos de Entidade"
            value={statistics.entity_stats?.length || 0}
            icon={<FileText className="h-8 w-8 text-purple-600" />}
          />
          <MetricCard
            title="Período"
            value={`${statistics.period_days || 0} dias`}
            icon={<TrendingUp className="h-8 w-8 text-orange-600" />}
          />
        </div>
      )}

      {tab === 'overview' && statistics && (
        <Card>
          <CardHeader>
            <CardTitle>Ações por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {statistics.action_stats?.map((stat, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className={`rounded px-2 py-1 text-xs font-medium ${getActionColor(stat.action)}`}>
                    {getActionIcon(stat.action)} {stat.action}
                  </span>
                  <div className="flex flex-1 items-center gap-4 pl-4">
                    <div className="h-2 flex-1 rounded-full bg-muted">
                      <div
                        className="h-2 rounded-full bg-primary"
                        style={{
                          width: `${statistics.action_stats[0].count ? (stat.count / statistics.action_stats[0].count) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="w-12 text-right font-medium">{stat.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'logs' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter size={18} /> Filtros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
                <select
                  value={filters.action}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                >
                  <option value="">Todas as Ações</option>
                  <option value="CREATE">CREATE</option>
                  <option value="UPDATE">UPDATE</option>
                  <option value="DELETE">DELETE</option>
                  <option value="LOGIN_SUCCESS">LOGIN_SUCCESS</option>
                  <option value="LOGIN_FAILED">LOGIN_FAILED</option>
                </select>
                <select
                  value={filters.entity_type}
                  onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                >
                  <option value="">Todas as Entidades</option>
                  <option value="driver">Driver</option>
                  <option value="vehicle">Vehicle</option>
                  <option value="contract">Contract</option>
                  <option value="trip">Trip</option>
                  <option value="user">User</option>
                </select>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                />
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                />
                <Button onClick={loadLogs}>
                  <Search size={16} className="mr-2" /> Buscar
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Logs de Auditoria ({logs.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2">Data/Hora</th>
                      <th>Usuário</th>
                      <th>Ação</th>
                      <th>Entidade</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="py-2">{formatDate(log.created_at)}</td>
                        <td>{log.username || 'Sistema'}</td>
                        <td>
                          <span className={`rounded px-2 py-1 text-xs font-medium ${getActionColor(log.action)}`}>
                            {getActionIcon(log.action)} {log.action}
                          </span>
                        </td>
                        <td>
                          <div>
                            <div className="font-medium">{log.entity_type}</div>
                            <div className="text-muted-foreground text-xs">{log.entity_name || `ID: ${log.entity_id}`}</div>
                          </div>
                        </td>
                        <td>{log.ip_address || '—'}</td>
                      </tr>
                    ))}
                    {logs.length === 0 && (
                      <tr>
                        <td colSpan="5" className="py-4 text-center text-sm text-muted-foreground">
                          Nenhum log encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'statistics' && statistics && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Atividade Diária (últimos {statistics.period_days} dias)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {statistics.daily_activity?.map((day, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-32 text-sm">{formatDate(day.date)}</div>
                    <div className="h-6 flex-1 rounded-full bg-muted">
                      <div
                        className="flex h-6 items-center justify-end rounded-full bg-primary pr-2"
                        style={{
                          width: `${statistics.daily_activity[0]?.count ? (day.count / statistics.daily_activity[0].count) * 100 : 0}%`,
                        }}
                      >
                        {day.count > 0 && <span className="text-xs text-primary-foreground">{day.count}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Entidades Mais Modificadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {statistics.entity_stats?.map((stat, idx) => (
                  <div key={idx} className="rounded-lg bg-muted p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium capitalize">{stat.entity_type}</div>
                        <div className="text-2xl font-bold text-primary">{stat.count}</div>
                      </div>
                      <FileText className="h-10 w-10 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function MetricCard({ title, value, icon }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}
